import React, { useState, useRef, useEffect } from 'react';
import { FORM_BORDER, FORM_BORDER_HOVER, FOCUS_RING, FORM_RADIUS, LABEL_TEXT } from './tokens';

// eslint-disable-next-line react-refresh/only-export-components
export const formatMoney = (num: number) => `$${num.toLocaleString('es-CL')}`;
// eslint-disable-next-line react-refresh/only-export-components
export const formatNumber = (num: number) => num.toLocaleString('es-CL');

interface MoneyInputProps {
  readonly value: number;
  readonly onChange: (value: number) => void;
  readonly min?: number;
  readonly max?: number;
  readonly label?: string;
  readonly required?: boolean;
  readonly disabled?: boolean;
  readonly className?: string;
  readonly showPrefix?: boolean;
}

export const MoneyInput: React.FC<MoneyInputProps> = ({
  value,
  onChange,
  min = 0,
  max,
  label,
  required = false,
  disabled = false,
  className = '',
  showPrefix = true
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const [editValue, setEditValue] = useState(String(value));
  const inputRef = useRef<HTMLInputElement>(null);

  // Sincronizar editValue cuando cambia el valor externamente (ej: botones de unidades)
  useEffect(() => {
    if (!isFocused) {
      setEditValue(String(value));
    }
  }, [value, isFocused]);

  const handleFocus = () => {
    setIsFocused(true);
    setEditValue(String(value));
    // Seleccionar todo el texto al hacer foco
    setTimeout(() => inputRef.current?.select(), 0);
  };

  const handleBlur = () => {
    setIsFocused(false);
    const num = Number(editValue) || 0;
    const clamped = Math.max(min, max !== undefined ? Math.min(num, max) : num);
    onChange(clamped);
    setEditValue(String(clamped));
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    // Permitir solo dígitos y vacío
    if (raw === '' || /^\d+$/.test(raw)) {
      setEditValue(raw);
      const num = Number(raw) || 0;
      onChange(num);
    }
  };

  const formattedValue = showPrefix ? formatMoney(value) : formatNumber(value);
  const displayValue = isFocused ? editValue : formattedValue;

  return (
    <div className={className}>
      {label && (
        <label className={`block ${LABEL_TEXT} mb-2`}>
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      <input
        ref={inputRef}
        type="text"
        inputMode="numeric"
        value={displayValue}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onChange={handleChange}
        disabled={disabled}
        className={`w-full px-4 py-3 ${FORM_BORDER} ${FORM_BORDER_HOVER} ${FORM_RADIUS} ${FOCUS_RING} transition-all text-lg ${
          disabled ? 'bg-slate-100 cursor-not-allowed' : 'bg-white'
        }`}
      />
    </div>
  );
};
